import { useState, useCallback } from 'react';
import { TREE_CHART_MODES } from '../../../constants/radar';
const useZoomPan = () => {
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastTouchDistance, setLastTouchDistance] = useState(null);

  // Separate zoom factors for each view
  const [treeZoomFactor, setTreeZoomFactor] = useState(1.0);
  const [radarZoomFactor, setRadarZoomFactor] = useState(1.0);

  const ZOOM_SPEED = 0.1;
  const PAN_SPEED = 1.5;
  const MAX_PAN_X = 2000;
  const MAX_PAN_Y = 4000;

  const ZOOM_CONSTRAINTS = {
    MIN_ZOOM: 0.5,
    MAX_ZOOM: 10.0,
  };

  const { MIN_ZOOM, MAX_ZOOM } = ZOOM_CONSTRAINTS;

  const constrainPan = (x, y) => ({
    x: Math.min(Math.max(x, -MAX_PAN_X), MAX_PAN_X),
    y: Math.min(Math.max(y, -MAX_PAN_Y), MAX_PAN_Y)
  });

  const handleWheel = useCallback((e) => {
    //e.preventDefault();
    if (e.ctrlKey || e.metaKey) {
      const delta = -e.deltaY * ZOOM_SPEED;
      setTreeZoomFactor(prev => {
        const newZoom = prev * (1 + delta);
        return Math.min(Math.max(newZoom, MIN_ZOOM), MAX_ZOOM);
      });
      setRadarZoomFactor(prev => {
        const newZoom = prev * (1 + delta);
        return Math.min(Math.max(newZoom, MIN_ZOOM), MAX_ZOOM);
      });
    } else {
      setPanPosition(prev => {
        const newX = prev.x - e.deltaX * PAN_SPEED;
        const newY = prev.y - e.deltaY * PAN_SPEED;
        return constrainPan(newX, newY);
      });
    }
  }, []);

  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      setLastTouchDistance(distance);
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    //e.preventDefault();
    //e.stopPropagation();
    
    if (e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );

      if (lastTouchDistance !== null) {
        const delta = distance - lastTouchDistance;
        setTreeZoomFactor(prev => {
          const newZoom = prev * (1 + delta * 0.01);
          return Math.min(Math.max(newZoom, MIN_ZOOM), MAX_ZOOM);
        });
        setRadarZoomFactor(prev => {
          const newZoom = prev * (1 + delta * 0.01);
          return Math.min(Math.max(newZoom, MIN_ZOOM), MAX_ZOOM);
        });
      }
      setLastTouchDistance(distance);
    } else if (e.touches.length === 1 && treeZoomFactor > 1) {
      const touch = e.touches[0];
      setPanPosition(prev => {
        const newX = prev.x + touch.movementX;
        const newY = prev.y + touch.movementY;
        return constrainPan(newX, newY);
      });
    }
  }, [lastTouchDistance, treeZoomFactor, radarZoomFactor]);

  const handleTouchEnd = useCallback(() => {
    setLastTouchDistance(null);
  }, []);

  const handleZoomIn = (chartType, isMaxZoom = false) => {
    if (chartType === TREE_CHART_MODES.TREE) {
      setTreeZoomFactor(prev => {
        if (isMaxZoom) {
          return MAX_ZOOM;
        }
        const newZoom = prev * 1.2;
        return Math.min(newZoom, MAX_ZOOM);
      });
    } else {
      setRadarZoomFactor(prev => {
        if (isMaxZoom) {
          return MAX_ZOOM;
        }
        const newZoom = prev * 1.2;
        return Math.min(newZoom, MAX_ZOOM);
      });
    }
  };

  const handleZoomOut = (chartType) => {
    if (chartType === TREE_CHART_MODES.TREE) {
      setTreeZoomFactor(prev => Math.max(prev * 0.8, MIN_ZOOM));
    } else {
      setRadarZoomFactor(prev => Math.max(prev * 0.8, MIN_ZOOM));
    }
  };

  const handleZoomTo = (factor, chartType) => {
    if (chartType === TREE_CHART_MODES.TREE) {
      setTreeZoomFactor(factor);
    } else {
      setRadarZoomFactor(factor);
    }
  };

  const handleMouseDown = (e) => {
    if (treeZoomFactor > 1) {
      //e.preventDefault();
      setIsDragging(true);
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && treeZoomFactor > 1) {
      //e.preventDefault();
      setPanPosition(prev => {
        const newX = prev.x + e.movementX;
        const newY = prev.y + e.movementY;
        return constrainPan(newX, newY);
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleResetPosition = (chartType) => {
    setPanPosition({ x: 0, y: 0 });
    if (chartType === TREE_CHART_MODES.TREE) {
      setTreeZoomFactor(1.0);
    } else {
      setRadarZoomFactor(1.0);
    }
  };

  const getZoomFactor = (chartType) => {
    return chartType === TREE_CHART_MODES.TREE ? treeZoomFactor : radarZoomFactor;
  };

  return {
    treeZoomFactor,
    radarZoomFactor,
    panPosition,
    isDragging,
    handleZoomIn,
    handleZoomOut,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleResetPosition,
    handleWheel,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleZoomTo,
    getZoomFactor,
  };
};

export default useZoomPan; 
